import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { motion } from 'framer-motion'

const { FiArrowLeft, FiCalendar, FiBook, FiCheck, FiClock, FiAward, FiRefreshCw } = FiIcons

const ChildProgressView = () => {
  const { childId } = useParams()
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const [child, setChild] = useState(null)
  const [completedLessons, setCompletedLessons] = useState([])
  const [dayProgress, setDayProgress] = useState([])
  const [loading, setLoading] = useState(true)
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  useEffect(() => {
    if (userProfile && childId) {
      fetchChildData()
    }
  }, [userProfile, childId])

  const fetchChildData = async () => {
    try {
      setLoading(true)

      // Verify this child belongs to the logged-in parent
      const { data: childData, error: childError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', childId)
        .eq('parent_id', userProfile.id)
        .single()

      if (childError || !childData) {
        console.error('Unauthorized or child not found')
        navigate('/dashboard')
        return
      }

      setChild(childData)

      // Assigned curricula for this student
      const { data: assignments, error: assignError } = await supabase
        .from('curriculum_student_assignments')
        .select('curriculum_id')
        .eq('student_id', childId)

      if (assignError) throw assignError
      const curriculumIds = (assignments || []).map((a) => a.curriculum_id)
      if (curriculumIds.length === 0) {
        setCompletedLessons([])
        setDayProgress([])
        return
      }

      // Courses in those curricula
      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('id')
        .in('curriculum_id', curriculumIds)
      if (coursesError) throw coursesError
      const courseIds = (courses || []).map((c) => c.id)
      if (courseIds.length === 0) {
        setCompletedLessons([])
        setDayProgress([])
        return
      }

      // All assigned lessons (from assigned curricula only)
      const { data: assignedLessons, error: lessonsError } = await supabase
        .from('lessons')
        .select('id, day_number')
        .in('course_id', courseIds)
      if (lessonsError) throw lessonsError
      const assignedLessonIds = new Set((assignedLessons || []).map((l) => l.id))

      // Completed progress for this child (only for assigned lessons)
      const { data: progressData, error: progressError } = await supabase
        .from('lesson_progress')
        .select(`
          *,
          lessons (
            id,
            title,
            subject,
            day_number
          )
        `)
        .eq('student_id', childId)
        .eq('completed', true)
        .order('completed_at', { ascending: false })

      if (progressError) throw progressError

      const completedForAssigned = (progressData || []).filter((p) =>
        assignedLessonIds.has(p.lesson_id)
      )
      setCompletedLessons(completedForAssigned)

      // Day stats: only days that have assigned lessons, grouped by day_number
      const dayMap = {}
      ;(assignedLessons || []).forEach((l) => {
        const d = Number(l.day_number) || 1
        if (!dayMap[d]) dayMap[d] = { total: 0, completed: 0 }
        dayMap[d].total += 1
      })
      completedForAssigned.forEach((p) => {
        const d = Number(p.lessons?.day_number)
        if (!isNaN(d) && dayMap[d]) dayMap[d].completed += 1
      })
      const dayStats = Object.entries(dayMap)
        .map(([day, { total, completed }]) => ({
          day: Number(day),
          completed,
          total,
          isComplete: total > 0 && completed === total
        }))
        .sort((a, b) => a.day - b.day)

      setDayProgress(dayStats)
    } catch (error) {
      console.error('Error fetching child data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleResetProgress = async () => {
    try {
      // Delete all progress for this child
      const { error } = await supabase
        .from('lesson_progress')
        .delete()
        .eq('student_id', childId)

      if (error) throw error

      // Refresh data
      await fetchChildData()
      setShowResetConfirm(false)
    } catch (error) {
      console.error('Error resetting progress:', error)
      alert('Failed to reset progress. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 rounded-full border-4 border-[#00AAFF]/30 border-t-[#00AAFF] animate-spin" />
      </div>
    )
  }

  if (!child) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Child not found or access denied</p>
      </div>
    )
  }

  const totalCompleted = completedLessons.length
  const totalLessons = dayProgress.reduce((sum, d) => sum + d.total, 0)
  const totalDaysComplete = dayProgress.filter((d) => d.isComplete).length
  const totalDaysWithLessons = dayProgress.length
  const overallProgress =
    totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0
  const currentDay =
    dayProgress.find((d) => !d.isComplete)?.day ?? (dayProgress[dayProgress.length - 1]?.day ?? 1)

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-gray-600 hover:text-[#00AAFF] mb-6 transition-colors"
      >
        <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
        Back to Dashboard
      </button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {child.first_name} {child.last_name}'s Progress
            </h1>
            <p className="text-blue-100 text-lg">
              Track learning achievements and milestones
            </p>
          </div>
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-4xl font-bold">
              {child.first_name.charAt(0)}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Overall Progress</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{overallProgress}%</p>
            </div>
            <SafeIcon icon={FiAward} className="w-8 h-8 text-[#00AAFF]" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Days Complete</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{totalDaysComplete}/{totalDaysWithLessons || 1}</p>
            </div>
            <SafeIcon icon={FiCalendar} className="w-8 h-8 text-green-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Lessons Done</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{totalCompleted}{totalLessons > 0 ? `/${totalLessons}` : ''}</p>
            </div>
            <SafeIcon icon={FiBook} className="w-8 h-8 text-purple-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Current Day</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">
                {currentDay}
              </p>
            </div>
            <SafeIcon icon={FiClock} className="w-8 h-8 text-amber-600" />
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Day Progress Calendar */}
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 mr-2 text-[#00AAFF]" />
              Progress by Day
            </h2>

            <div className="grid grid-cols-6 gap-3">
              {dayProgress.length === 0 ? (
                <p className="col-span-6 text-center text-gray-500 py-4">No curriculum assigned yet. Assign a curriculum to see day-by-day progress.</p>
              ) : (
              dayProgress.map((day) => (
                <div
                  key={day.day}
                  className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center p-2 transition-all ${
                    day.isComplete
                      ? 'bg-green-50 border-green-500'
                      : day.completed > 0
                      ? 'bg-amber-50 border-amber-500'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <span className="text-xs font-bold text-gray-600">D{day.day}</span>
                  {day.isComplete && (
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600 mt-1" />
                  )}
                  {!day.isComplete && day.completed > 0 && (
                    <span className="text-xs text-amber-600 mt-1">
                      {day.completed}/{day.total}
                    </span>
                  )}
                </div>
              ))
              )}
            </div>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded bg-green-500 mr-2" />
                <span className="text-gray-600">Complete</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded bg-amber-500 mr-2" />
                <span className="text-gray-600">In Progress</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded bg-gray-200 mr-2" />
                <span className="text-gray-600">Not Started</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Recent Completions & Actions */}
        <div className="space-y-6">
          {/* Recent Lessons */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiBook} className="w-5 h-5 mr-2 text-[#00AAFF]" />
              Recent Lessons
            </h3>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {completedLessons.slice(0, 10).map((progress, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <h4 className="font-semibold text-sm text-gray-800">
                    {progress.lessons?.title}
                  </h4>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-600">
                      {progress.lessons?.subject}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(progress.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}

              {completedLessons.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  No completed lessons yet
                </p>
              )}
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4">Actions</h3>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="w-full flex items-center justify-center px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors border border-red-200"
            >
              <SafeIcon icon={FiRefreshCw} className="w-5 h-5 mr-2" />
              Reset Progress
            </button>

            <p className="text-xs text-gray-500 mt-3 text-center">
              Use this to restart your child's curriculum
            </p>
          </motion.div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-8 max-w-md w-full"
          >
            <h3 className="text-2xl font-bold text-red-600 mb-4">Reset Progress?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to reset all progress for {child.first_name}?
              This action cannot be undone and will delete all completed lesson records.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetProgress}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reset Progress
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default ChildProgressView
